import { useEffect, useState } from "react";
import { CircleUser, LogOut, Phone, Mail, CalendarDays } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { inr, fmtDate, initials } from "@/lib/format";
import { PageHeader, Loading } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";

export default function Profile() {
  const { user, logout } = useAuth();
  const [dash, setDash] = useState(null);

  useEffect(() => {
    if (user?.role === "physiotherapist") api.get("/dashboard/physio").then((r) => setDash(r.data)).catch(() => {});
  }, [user]);

  if (!user) return <Loading />;
  const roleLabel = { admin: "Admin / Owner", receptionist: "Receptionist", physiotherapist: "Home Physiotherapist" }[user.role];

  return (
    <div>
      <PageHeader title="My Profile" testid="profile-page" />
      <Card className="p-6 mb-4">
        <div className="flex items-center gap-4">
          <Avatar className="h-16 w-16"><AvatarFallback className="bg-bk-lightGreen text-bk-green text-xl font-bold">{initials(user.name)}</AvatarFallback></Avatar>
          <div>
            <p className="font-display font-extrabold text-xl text-bk-blue">{user.name}</p>
            <Badge className="bg-bk-green capitalize mt-1">{roleLabel}</Badge>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3 mt-5 text-sm">
          <div className="flex items-center gap-2 text-slate-600"><Mail className="h-4 w-4 text-slate-400" /> {user.email}</div>
          <div className="flex items-center gap-2 text-slate-600"><Phone className="h-4 w-4 text-slate-400" /> {user.phone || "-"}</div>
          <div className="flex items-center gap-2 text-slate-600"><CalendarDays className="h-4 w-4 text-slate-400" /> Joined {fmtDate(user.joining_date)}</div>
        </div>
      </Card>

      {dash && (
        <Card className="p-5 mb-4">
          <p className="font-display font-bold text-slate-700 mb-3">This Month</p>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div><p className="font-display font-extrabold text-2xl text-bk-blue tabular-nums">{dash.month.visits}</p><p className="text-xs text-slate-400">Visits</p></div>
            <div><p className="font-display font-extrabold text-2xl text-bk-green tabular-nums">{inr(dash.month.collection)}</p><p className="text-xs text-slate-400">Collection</p></div>
            <div><p className="font-display font-extrabold text-2xl text-bk-info tabular-nums">{dash.month.assigned_patients}</p><p className="text-xs text-slate-400">Patients</p></div>
          </div>
        </Card>
      )}

      <Button onClick={logout} variant="outline" className="text-bk-danger w-full sm:w-auto" data-testid="profile-logout"><LogOut className="h-4 w-4 mr-1" /> Logout</Button>
    </div>
  );
}
